// src/features/dashboard/AdminDashboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  fetchAllUsers,
  adminAddUser,
  adminUpdateUser,
  adminDeleteUser,
  bulkAddStudents,
  syncOrganisationPermissions,
} from "../../services/sheetDataService";
import UserForm from "./admin/UserForm";
import { StatsSection } from "./admin/StatsSection";
import { UsersTable } from "./admin/UsersTable";
import { ClassesTable } from "./admin/ClassesTable";
import { ClassFormInline } from "./admin/ClassFormInline";

const AdminDashboard = () => {
  const { accessToken, user } = useAuth();
  const usersSpreadsheetId = user?.usersSpreadsheetId || user?.spreadsheetId;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userRoleFilter, setUserRoleFilter] = useState("All");

  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [showClassForm, setShowClassForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);

  const [activeTab, setActiveTab] = useState('users');

  // For manual sync button
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');

  const isSuperAdmin = user?.role?.toLowerCase() === 'superadmin';

  const orgDefaults = {
    organisationId: user?.organisationId || '',
    school: user?.school || '',
    province: user?.province || '',
    district: user?.district || '',
    circuit: user?.circuit || '',
  };

  const teacherOptions = useMemo(() => {
    return users
      .filter(u => u.role?.toLowerCase() === 'teacher')
      .map(u => ({
        email: u.email,
        name: u.name || '',
        surname: u.surname || '',
        display: `${u.surname || ''}, ${u.name || ''} (${u.email})`.trim()
      }));
  }, [users]);

  const stats = useMemo(() => {
    const students = users.filter(u => u.role?.toLowerCase() === "student").length;
    const teachers = users.filter(u => u.role?.toLowerCase() === "teacher").length;
    const admins = users.filter(u => u.role?.toLowerCase() === "admin").length;

    const classMap = new Map();
    users.filter(u => u.role?.toLowerCase() === "student").forEach(u => {
      if (u.teacher && u.subject && u.grade) {
        const key = `${u.teacher}|${u.subject}|${u.grade}`;
        if (!classMap.has(key)) {
          classMap.set(key, {
            teacher: u.teacher,
            subject: u.subject,
            grade: u.grade,
            studentCount: 0,
            key,
            students: [],
            googleClassroomLink: u.googleClassroomLink || '',
            zoomLink: u.zoomLink || '',
            zoomPassword: u.zoomPassword || '',
            schedule: u.schedule || [],
            termData: u.termData || [], // 👈 ADD THIS
          });
        }
        classMap.get(key).studentCount++;
        classMap.get(key).students.push(u);
      }
    });

    return {
      students,
      teachers,
      admins,
      classes: classMap.size,
      classList: Array.from(classMap.values()),
    };
  }, [users]);

  useEffect(() => {
    const fetchData = async () => {
      if (!accessToken) return setError("Not authenticated") && setLoading(false);
      if (!usersSpreadsheetId) return setError("Organisation data not available.") && setLoading(false);
      try {
        const usersData = await fetchAllUsers(accessToken, usersSpreadsheetId);
        setUsers(usersData || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accessToken, usersSpreadsheetId]);

  // ---- Manual Sync for this Organisation ----
  /*const handleSyncPermissions = async () => {
    if (!window.confirm('This will sync spreadsheet permissions for all users in this organisation based on their status. Proceed?')) {
      return;
    }

    setSyncing(true);
    setSyncProgress('Syncing...');

    try {
      const spreadsheetIds = [
        user.usersSpreadsheetId || user.spreadsheetId,
        user.attendanceSpreadsheetId || user.spreadsheetId,
        user.marksSpreadsheetId || user.spreadsheetId,
        user.topicsSpreadsheetId || user.spreadsheetId,
      ].filter(Boolean);

      if (spreadsheetIds.length === 0) {
        alert('No spreadsheet IDs found for this organisation.');
        setSyncing(false);
        setSyncProgress('');
        return;
      }

      await syncOrganisationPermissions(accessToken, user.organisationId, spreadsheetIds);
      setSyncProgress('✅ Sync complete!');
      alert('✅ Permissions synced successfully!');
    } catch (err) {
      console.error('Sync failed:', err);
      setSyncProgress('❌ Sync failed');
      alert('❌ Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncProgress(''), 4000);
    }
  };*/

  // ---- User handlers ----
  const handleAddUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };
  const handleEditUser = (u) => {
    setEditingUser(u);
    setShowUserForm(true);
  };
  const handleCancelUserForm = () => {
    setShowUserForm(false);
    setEditingUser(null);
  };
  const handleSaveUser = async (userData) => {
    try {
      const dataToSave = { ...userData, role: userData.role || 'Student' };
      const attSheetId = user.attendanceSpreadsheetId || user.spreadsheetId;
      const marksSheetId = user.marksSpreadsheetId || user.spreadsheetId;
      if (editingUser) {
        await adminUpdateUser(accessToken, usersSpreadsheetId, editingUser.rowNumber, dataToSave, attSheetId, marksSheetId);
      } else {
        await adminAddUser(accessToken, usersSpreadsheetId, dataToSave);
      }
      const updatedUsers = await fetchAllUsers(accessToken, usersSpreadsheetId);
      setUsers(updatedUsers);
      setShowUserForm(false);
      setEditingUser(null);
    } catch (err) {
      alert(err.message || 'Failed to save user.');
    }
  };
  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} ${u.surname}?`)) return;
    try {
      await adminDeleteUser(accessToken, usersSpreadsheetId, u);
      const updatedUsers = await fetchAllUsers(accessToken, usersSpreadsheetId);
      setUsers(updatedUsers);
    } catch (err) {
      alert('Failed to delete user.');
    }
  };

  // ---- Class handlers ----
  const handleManageClass = (cls) => {
    setEditingClass(cls || null);
    setShowClassForm(true);
  };
  const handleCancelClassForm = () => {
    setShowClassForm(false);
    setEditingClass(null);
  };

  /*const handleSaveClass = async (classData, importedStudents = [], newStudentsData = []) => {
    // Log incoming data for debugging
    console.log('📊 handleSaveClass received:', {
      classData,
      importedStudents: importedStudents.length,
      newStudentsData: newStudentsData.length,
      editingClass: editingClass?.key,
    });

    // Ensure grade is set
    const grade = classData.grade || 'N/A';

    try {
      // 1. Update existing students (already in the class)
      const existingStudents = editingClass?.students || [];
      const updatePromises = existingStudents.map(student => {
        const userData = {
          ...student,
          subject: classData.subject,
          grade: grade,
          teacher: classData.teacher,
          googleClassroomLink: classData.googleClassroomLink || student.googleClassroomLink,
          zoomLink: classData.zoomLink || student.zoomLink,
          zoomPassword: classData.zoomPassword || student.zoomPassword,
          schedule: classData.schedule && classData.schedule.length > 0 ? classData.schedule : student.schedule,
          status: student.status || 'active', // 👈 preserve status
        };
        return adminUpdateUser(
          accessToken,
          usersSpreadsheetId,
          student.rowNumber,
          userData,
          user.attendanceSpreadsheetId || user.spreadsheetId,
          user.marksSpreadsheetId || user.spreadsheetId
        );
      });

      // 2. Import students from another class (new rows via bulkAddStudents)
      if (importedStudents.length > 0) {
        const metadata = {
          organisationId: user.organisationId || 'N/A',
          school: user.school || 'N/A',
          province: user.province || 'N/A',
          district: user.district || 'N/A',
          circuit: user.circuit || 'N/A',
          teacher: classData.teacher || user.email,
          subject: classData.subject,
          grade: grade,
          googleClassroomLink: classData.googleClassroomLink || '',
          schedule: classData.schedule || [],
          zoomLink: classData.zoomLink || '',
          zoomPassword: classData.zoomPassword || '',
          studentType: 'group',
        };
        const studentsArray = importedStudents.map(s => ({
          name: s.name,
          surname: s.surname,
          email: s.email,
          gender: s.gender || '',
        }));
        await bulkAddStudents(accessToken, usersSpreadsheetId, studentsArray, metadata);
      }

      // 3. Create new students from the batch table (via adminAddUser)
      if (newStudentsData.length > 0) {
        for (const student of newStudentsData) {
          const userData = {
            name: student.name,
            surname: student.surname,
            email: student.email,
            role: 'Student',
            organisationId: user.organisationId || '',
            school: user.school || '',
            province: user.province || '',
            district: user.district || '',
            circuit: user.circuit || '',
            grade: grade,
            subject: classData.subject,
            teacher: classData.teacher,
            googleClassroomLink: classData.googleClassroomLink || '',
            zoomLink: classData.zoomLink || '',
            zoomPassword: classData.zoomPassword || '',
            schedule: classData.schedule || [],
            studentType: 'group',
            gender: student.gender || '',
            status: 'active', // 👈 new students are active
          };
          await adminAddUser(accessToken, usersSpreadsheetId, userData);
        }
      }

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      const updatedUsers = await fetchAllUsers(accessToken, usersSpreadsheetId);
      setUsers(updatedUsers);
      setShowClassForm(false);
      setEditingClass(null);

      const total = existingStudents.length + importedStudents.length + newStudentsData.length;
      alert(`✅ ${total} student(s) processed (${existingStudents.length} updated, ${importedStudents.length} imported, ${newStudentsData.length} created).`);
    } catch (err) {
      console.error('❌ handleSaveClass error:', err);
      alert('❌ Failed to update class: ' + err.message);
    }
  };*/

  const handleSaveClass = async (classData, importedStudents = [], newStudentsData = []) => {
    // Log incoming data for debugging
    console.log('📊 handleSaveClass received:', {
      classData,
      importedStudents: importedStudents.length,
      newStudentsData: newStudentsData.length,
      editingClass: editingClass?.key,
    });

    // Ensure grade is set
    const grade = classData.grade || 'N/A';

    try {
      // 1. Update existing students (already in the class)
      const existingStudents = editingClass?.students || [];
      const updatePromises = existingStudents.map(student => {
        const userData = {
          ...student,
          subject: classData.subject,
          grade: grade,
          teacher: classData.teacher,
          googleClassroomLink: classData.googleClassroomLink || student.googleClassroomLink,
          zoomLink: classData.zoomLink || student.zoomLink,
          zoomPassword: classData.zoomPassword || student.zoomPassword,
          schedule: classData.schedule && classData.schedule.length > 0 ? classData.schedule : student.schedule,
          // 👇 ADD TERMDATA
          termData: classData.termData && classData.termData.length > 0 ? classData.termData : (student.termData || []),
          status: student.status || 'active',
        };
        return adminUpdateUser(
          accessToken,
          usersSpreadsheetId,
          student.rowNumber,
          userData,
          user.attendanceSpreadsheetId || user.spreadsheetId,
          user.marksSpreadsheetId || user.spreadsheetId
        );
      });

      // 2. Import students from another class (new rows via bulkAddStudents)
      if (importedStudents.length > 0) {
        const metadata = {
          organisationId: user.organisationId || 'N/A',
          school: user.school || 'N/A',
          province: user.province || 'N/A',
          district: user.district || 'N/A',
          circuit: user.circuit || 'N/A',
          teacher: classData.teacher || user.email,
          subject: classData.subject,
          grade: grade,
          googleClassroomLink: classData.googleClassroomLink || '',
          schedule: classData.schedule || [],
          // 👇 ADD TERMDATA
          termData: classData.termData || [],
          zoomLink: classData.zoomLink || '',
          zoomPassword: classData.zoomPassword || '',
          studentType: 'group',
        };
        const studentsArray = importedStudents.map(s => ({
          name: s.name,
          surname: s.surname,
          email: s.email,
          gender: s.gender || '',
        }));
        await bulkAddStudents(accessToken, usersSpreadsheetId, studentsArray, metadata);
      }

      // 3. Create new students from the batch table (via adminAddUser)
      if (newStudentsData.length > 0) {
        for (const student of newStudentsData) {
          const userData = {
            name: student.name,
            surname: student.surname,
            email: student.email,
            role: 'Student',
            organisationId: user.organisationId || '',
            school: user.school || '',
            province: user.province || '',
            district: user.district || '',
            circuit: user.circuit || '',
            grade: grade,
            subject: classData.subject,
            teacher: classData.teacher,
            googleClassroomLink: classData.googleClassroomLink || '',
            zoomLink: classData.zoomLink || '',
            zoomPassword: classData.zoomPassword || '',
            schedule: classData.schedule || [],
            termData: classData.termData || [],
            studentType: 'group',
            gender: student.gender || '',
            status: 'active',
          };
          await adminAddUser(accessToken, usersSpreadsheetId, userData);
        }
      }

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      const updatedUsers = await fetchAllUsers(accessToken, usersSpreadsheetId);
      setUsers(updatedUsers);
      setShowClassForm(false);
      setEditingClass(null);

      const total = existingStudents.length + importedStudents.length + newStudentsData.length;
      alert(`✅ ${total} student(s) processed (${existingStudents.length} updated, ${importedStudents.length} imported, ${newStudentsData.length} created).`);
    } catch (err) {
      console.error('❌ handleSaveClass error:', err);
      alert('❌ Failed to update class: ' + err.message);
    }
  };

  if (loading) return <p className="text-center mt-10">Loading dashboard...</p>;
  if (error) return <p className="text-center mt-10 text-red-500">{error}</p>;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        {/*<div className="flex items-center gap-3">
          <button
            onClick={handleSyncPermissions}
            disabled={syncing}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium shadow"
          >
            {syncing ? '⏳ Syncing...' : '🔒 Sync Permissions'}
          </button>
          {syncProgress && (
            <span className="text-sm text-gray-600">{syncProgress}</span>
          )}
        </div> */}
      </div>

      <StatsSection stats={stats} />

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-4">
          <button
            onClick={() => { setActiveTab('users'); setShowUserForm(false); setShowClassForm(false); }}
            className={`py-2 px-4 text-sm font-medium ${activeTab === 'users' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Users
          </button>
          <button
            onClick={() => { setActiveTab('classes'); setShowUserForm(false); setShowClassForm(false); }}
            className={`py-2 px-4 text-sm font-medium ${activeTab === 'classes' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Classes ({stats.classes})
          </button>
        </nav>
      </div>

      {activeTab === 'users' && (
        <>
          {showUserForm ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold mb-4">{editingUser ? 'Edit User' : 'Add User'}</h2>
              <UserForm
                user={editingUser}
                defaultValues={orgDefaults}
                onSubmit={handleSaveUser}
                onCancel={handleCancelUserForm}
                isSuperAdmin={isSuperAdmin}
                isOrganisationReadOnly={!isSuperAdmin}
                teacherOptions={teacherOptions}
              />
            </div>
          ) : (
            <UsersTable
              users={users}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              roleFilter={userRoleFilter}
              setRoleFilter={setUserRoleFilter}
            />
          )}
        </>
      )}

      {activeTab === 'classes' && (
        <>
          {showClassForm ? (
            <ClassFormInline
              classData={editingClass}
              onSave={handleSaveClass}
              onCancel={handleCancelClassForm}
              teacherOptions={teacherOptions}
              allStudents={users}
              allClasses={stats.classList}
            />
          ) : (
            <ClassesTable
              classes={stats.classList}
              onManageClass={handleManageClass}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;